import { createRouter, createWebHashHistory } from 'vue-router'

import { events$ } from '@/services'
import useSession from '@/hooks/useSession'
import useLogger from '@/hooks/useLogger'
import useTranslation from '@/hooks/useTranslation'

import { EVENT_TRACKING } from '@/config/events'

const { logError } = useLogger()

/**
 * Load all route-components
 */
async function getRoutes() {
  const data = []
  const routes = [
    'advisoryProtocol',
    'application',
    'basket',
    'digitalSignature',
    'nextSteps',
    'notFound',
    'onboarding',
    'partner',
    'person',
    'product',
    'reentry',
    'submit',
    'aide',
  ]

  for (const route of routes) {
    const file = await import(`@/modules/Configurator/routes/${route}.js`)
    file.default.forEach(item => {
      data.push(item)
    })
  }

  data.push({
    id: 'root',
    name: 'root',
    path: '/',
    redirect: '/persons',
  })
  return data
}

const Router = createRouter({
  history: createWebHashHistory(),
  routes: await getRoutes(),
  scrollBehavior(to, _from, _position) {
    if (to.meta?.scrollTop === false) return

    window.setTimeout(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      })
    }, 50)
  },
})

Router.beforeEach(async (to, from, next) => {
  const { isActive, autostart } = useSession()
  const { browserLanguage } = useTranslation()

  const __language = browserLanguage?.toUpperCase()

  const disableNavigation = !!(
    (window.OVP_APP_DISABLE_NAVIGATION || from.meta.disableNavigation) &&
    !to.query?.forcedRouting
  )

  if (disableNavigation) {
    next(false)
  } else {
    const isPrivate = to.meta?.secured ?? true
    if (isPrivate && !isActive.value) {
      try {
        await autostart({ language: __language }, false)
      } catch (_e) {
        // issue with resuming/generating session => restart with fresh session
        await autostart({ language: __language }, true)
      }
    }

    next()
  }
})

Router.afterEach((to, from) => {
  try {
    // we expect a sessionStorage item to be set, otherwise nothing will trigger
    events$.emit(EVENT_TRACKING.ROUTE_CHANGE, { opts: { strict: true }, to, from })
  } catch (error) {
    logError(error)
  }
  if (window.ineum) {
    window.ineum('page', to.name)
  }
})

export default Router
